import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import Typography from 'antd/lib/typography';
import { ArrowRightOutlined } from '@ant-design/icons';
import { authTypeForIntegrationType, displayNameForIntegrationType } from '../../helpers/integrationHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectOrgProviders,
    selectIntegrationFromIntegrationType,
    selectAppStoreType,
    selectIsBuildDistroEnabled,
} from '../../selectors/selectors';
import { IntegrationType, SettingType } from '../../types';
import { antGrey } from '../styles/globalStyles';
import { ConnectButtonForIntegrationType } from '../ConnectButtonForIntegrationType';
import { AppComponentProps, AppRouteComponentMatchParams, SetupStepsCallerContext } from '../../typescriptHelpers';
import { displayTextForIntegrationStatus, tagColorForIntegrationStatus } from '../../helpers';
import { Link } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { disabledIntegrationMessageForContext } from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import IntegrationIcon from '../icons/IntegrationIcon';
import { CreateIntegrationRequestDataType } from '../../actions/integrations';
import { useIsLoading } from '@/utils/hooks/helpers';

interface IntegrationCardProps extends AppComponentProps {
    integrationType: IntegrationType;
    isConnected: boolean;
    onClickIntegrationShowInstallationModal: (integrationType: IntegrationType) => void;
    onClickShowProviderModal: (integrationType: IntegrationType) => void;
    addIntegration: (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => void;
    context?: SetupStepsCallerContext;
    disabled?: boolean;
}

export const IntegrationCard = (props: IntegrationCardProps) => {
    const {
        integrationType,
        appId,
        orgId,
        onClickIntegrationShowInstallationModal,
        onClickShowProviderModal,
        addIntegration,
        disabled,
        context,
    } = props;
    const { orgIdParam } = useParams<AppRouteComponentMatchParams>();
    const integrationDisplayName = displayNameForIntegrationType(integrationType);
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));
    const isBuildDistroEnabled = useSelector((state: RootState) => selectIsBuildDistroEnabled(state, props.appId));

    const authType = authTypeForIntegrationType(integrationType);

    const integrationIsLoading = useIsLoading(authType);

    const integration = useSelector((state: RootState) =>
        integrationType ? selectIntegrationFromIntegrationType(state, appId, integrationType) : null,
    );

    const integrationStatus = integration ? displayTextForIntegrationStatus(integration, true) : '';
    const integrationStatusColor = integration ? tagColorForIntegrationStatus(integration, true) : '';

    const disabledMessageForContext = disabled && context ? disabledIntegrationMessageForContext(context) : null;

    const disableRunwayBetaIntegration =
        integrationType === IntegrationType.RunwayBuildDistroBeta && !isBuildDistroEnabled;

    const onClickInstallRunwayIntegration = (integrationType: IntegrationType) => {
        addIntegration(integrationType, {});
    };

    return (
        <div
            style={{
                backgroundColor: '#FFF',
                border: `1px solid ${integrationStatusColor}`,
                borderRadius: 8,
                boxShadow: `4px 4px 0px 0px #0000001A`,
                height: 90,
                padding: 25,
                display: 'flex',
                alignItems: 'center',
                gap: 25,
            }}
        >
            <IntegrationIcon size={36} integrationId={integrationType} />
            <Typography.Text style={{ fontSize: 28, color: antGrey.grey8, flex: 1 }}>
                {integrationDisplayName}
            </Typography.Text>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                {integrationStatus ?
                    <Link
                        to={{
                            pathname: createAppSettingsRouteWithSettingType(orgId, appId, SettingType.Integrations),
                            state: {
                                scrollTo: integration?.capabilities[0],
                            },
                        }}
                    >
                        <Button
                            type="primary"
                            size="large"
                            style={{
                                backgroundColor: integrationStatusColor,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                textShadow: 'none',
                                border: 'none',
                                color: 'white',
                            }}
                        >
                            {integrationStatus}
                        </Button>
                    </Link>
                :   <ConnectButtonForIntegrationType
                        integrationType={integrationType}
                        appPlatform={appPlatform}
                        appStoreType={appStoreType}
                        orgId={orgId}
                        orgIdParam={orgIdParam}
                        appIdParam={appId}
                        integrationProviders={integrationProviders}
                        onClickShowInstallationModal={onClickIntegrationShowInstallationModal}
                        onClickShowProviderModal={onClickShowProviderModal}
                        onClickInstallRunwayIntegration={onClickInstallRunwayIntegration}
                        type="primary"
                        size="large"
                        icon={<ArrowRightOutlined />}
                        disabled={disabled || disableRunwayBetaIntegration}
                        loading={integrationIsLoading}
                    />
                }
                {disabledMessageForContext && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {disabledMessageForContext}
                    </Text>
                )}
            </div>
        </div>
    );
};
